import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMail, FiMapPin, FiClock, FiCheckCircle, FiTool, FiBattery, FiSettings, FiShield } from 'react-icons/fi';

const GolfCartRepairService = () => {
  const services = [
    {
      icon: FiBattery,
      title: 'Battery Services',
      description: 'Complete battery testing, replacement, and maintenance for all golf cart models',
      features: ['Battery testing', 'Water level checks', 'Cable cleaning', 'Full replacement']
    },
    {
      icon: FiTool,
      title: 'Motor Repairs',
      description: 'Expert electric and gas motor repairs, rebuilds, and performance upgrades',
      features: ['Motor diagnostics', 'Bearing replacement', 'Brush replacement', 'Performance tuning']
    },
    {
      icon: FiSettings,
      title: 'Controller Service',
      description: 'Programming, diagnostics, and replacement of speed controllers',
      features: ['Speed programming', 'Error code diagnosis', 'Controller upgrades', 'Throttle calibration']
    },
    {
      icon: FiShield,
      title: 'Preventive Maintenance',
      description: 'Regular maintenance packages to keep your golf cart running smoothly',
      features: ['Multi-point inspection', 'Fluid checks', 'Brake adjustments', 'Tire service']
    }
  ];

  const brands = [
    'Club Car', 'EZ-GO', 'Yamaha', 'Star EV', 'Cushman', 'Bad Boy Buggies'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      <Helmet>
        <title>Golf Cart Repair Services | Expert Maintenance & Repairs | Paradise-Performance</title>
        <meta name="description" content="Professional golf cart repair services in Paradise, TX. Expert electric and gas golf cart repairs, battery service, motor repairs, and preventive maintenance for all major brands." />
        <link rel="canonical" href="https://paradise-performance.com/golf-cart-repair" />
      </Helmet>

      {/* Professional Hero Section - Full Height */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image Container */}
        <div className="absolute inset-0 z-0">
          {/* Placeholder for image - you can add your golf cart image here */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-green-900"></div>
          <div className="absolute inset-0 bg-black/40"></div>
          
          {/* Professional overlay pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Subtitle */}
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="inline-block text-emerald-300 text-lg md:text-xl font-medium mb-4 tracking-wider uppercase"
            >
              Paradise-Performance
            </motion.span>

            {/* Main Title */}
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-5xl md:text-7xl font-bold mb-6 text-white leading-tight"
            >
              Expert Golf Cart
              <span className="block text-emerald-400">Repair Services</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-xl md:text-2xl mb-10 text-gray-200 max-w-3xl mx-auto leading-relaxed"
            >
              Professional maintenance and repair for all makes and models of golf carts.
              Known by North Texas golf courses and clients for our high-quality service.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-10 py-5 bg-emerald-500 text-white rounded-full font-semibold text-lg hover:bg-emerald-400 transition-all transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <FiPhone className="mr-3 text-xl" />
                Schedule Service Today
              </Link>
              <a
                href="tel:8173822316"
                className="inline-flex items-center justify-center px-10 py-5 border-2 border-white/80 text-white rounded-full font-semibold text-lg hover:bg-white hover:text-emerald-900 transition-all transform hover:scale-105"
              >
                <FiPhone className="mr-3 text-xl" />
                (817) 382-2316
              </a>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-12 flex flex-wrap justify-center gap-8 text-white/80"
            >
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-emerald-400" />
                <span>All Major Brands</span>
              </div>
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-emerald-400" />
                <span>Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-emerald-400" />
                <span>Warranty on All Repairs</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Comprehensive Golf Cart Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From routine maintenance to complex repairs, our expert technicians handle it all
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <service.icon className="w-12 h-12 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <FiCheckCircle className="w-4 h-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Service */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Brands We Service
            </h2>
            <p className="text-lg text-gray-600">
              Expert repairs for all major golf cart manufacturers
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-8">
            {brands.map((brand, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-md px-8 py-4 hover:shadow-lg transition-shadow"
              >
                <span className="text-lg font-semibold text-gray-700">{brand}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Why Choose Paradise-Performance?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start">
                <FiCheckCircle className="w-6 h-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">expert technicians</h3>
                  <p className="text-gray-600">Factory-trained experts with years of experience</p>
                </div>
              </div>
              <div className="flex items-start">
                <FiCheckCircle className="w-6 h-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Quality Parts</h3>
                  <p className="text-gray-600">OEM and premium aftermarket parts only</p>
                </div>
              </div>
              <div className="flex items-start">
                <FiCheckCircle className="w-6 h-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Fast Service</h3>
                  <p className="text-gray-600">Most repairs completed within 24-48 hours</p>
                </div>
              </div>
              <div className="flex items-start">
                <FiCheckCircle className="w-6 h-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Warranty Coverage</h3>
                  <p className="text-gray-600">All repairs backed by our service guarantee</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Schedule Your Golf Cart Service?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Contact us today for expert repairs and maintenance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <FiMail className="mr-2" />
              Contact Us Online
            </Link>
            <div className="text-center">
              <p className="mb-2">Or Call Us Directly</p>
              <a
                href="tel:940-349-0038"
                className="text-2xl font-bold hover:underline"
              >
                (817) 382-2316
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-6">Visit Our Service Center</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center">
                <FiMapPin className="w-8 h-8 text-primary-600 mb-3" />
                <h4 className="font-semibold mb-1">Location</h4>
                <p className="text-gray-600">116 W Garza Ave<br />Paradise, TX 76073</p>
              </div>
              <div className="flex flex-col items-center">
                <FiClock className="w-8 h-8 text-primary-600 mb-3" />
                <h4 className="font-semibold mb-1">Hours</h4>
                <p className="text-gray-600">Mon-Fri: 8:00 AM - 6:00 PM<br />Sat: 9:00 AM - 3:00 PM</p>
              </div>
              <div className="flex flex-col items-center">
                <FiPhone className="w-8 h-8 text-primary-600 mb-3" />
                <h4 className="font-semibold mb-1">Contact</h4>
                <p className="text-gray-600">(817) 382-2316<br />service@paradise-performance.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default GolfCartRepairService;
